module Client.MFA

open Elmish
open Fable.Remoting.Client
open Shared
open Shared.LogIn
open Illy.SafeStack
open Illy.SafeStack.Client
open Illy.SafeStack.Validation
open Fable.React 
open Fable.React.Props 
open SafeStack.Client


type Model =
    {
        Code: CodeEntry.Model
        ReSent: bool
        Error: string option
        Mode: MFAMode
        Busy: bool
    }

type Msg =
    | Submit
    | MFAResponse of MFAResult
    | CodeMsg of CodeEntry.Msg
    | StorageError of exn
    | ApiError of ApiError
    | LoggedIn
    | MFAStatusCheckResult of MFAMode
    | UpdatedMFASeed of unit
    | UpdateMFASeed
    | ClearCode

module Calls =
    let checkMFAState() = Api.mfaCall<IMfaApi,_,_,_> (fun api -> api.checkMFAState) () MFAStatusCheckResult ApiError
    let updateMFASeed seed = Api.mfaCall<IMfaApi,_,_,_> (fun api -> api.submitAppKey) seed UpdatedMFASeed ApiError

module Initialisation =
    let code size = CodeEntry.init "mfaCode" size (CodeEntry.CustomSettings.defaults)

let init() =
    {
        Code = Initialisation.code 6
        Error = None
        ReSent = false
        Mode = MFAMode.Email true
        Busy = false
    }, Calls.checkMFAState()

let update msg model =
    match msg with
    | StorageError exn ->
#if DEBUG
        printfn "There was a problem saving your login to the browser: %O" exn
#endif
        model, Cmd.none 
    | ApiError exn ->
        { model with
            Error = Some <| exn.ToString()
            Busy = false
        }, Cmd.none
    | Submit ->
        let code = model.Code |> CodeEntry.toString
        let user = Storage.tryGet<ClientSideUser> Storage.USER_STORAGE_KEY

        match user with
        | None ->
            model, Routing.PageRoute.Login |> Routing.PageRoute.toPath |> Elmish.Navigation.Navigation.newUrl
        | Some _ ->
            { model with 
                Error = None 
                ReSent = false
                Busy = true
            }, Api.mfaCall<IMfaApi,_,_,_> (fun x -> x.submit) code MFAResponse ApiError
    | CodeMsg cmsg ->
        let tmod, tse = CodeEntry.update cmsg model.Code
        
        match tse with
        | SideEffect.Cmd tcmd -> { model with Code = tmod }, Cmd.map CodeMsg tcmd
        | SideEffect.Event ev ->
            match ev with
            | CodeEntry.Event.Submit _ -> { model with Code = tmod }, Cmd.ofMsg Submit
            | CodeEntry.Event.StartedEntry -> { model with Code = tmod; Error = None }, Cmd.none

    | ClearCode ->
        {
            model with Code = model.Code |> CodeEntry.resetText
        }, Cmd.none
    | MFAResponse response ->
        match response with
        | Shared.Types.MFAResult.Expired ->
            {
                model with
                    Code = CodeEntry.resetText model.Code
                    ReSent = true
            }, Cmd.none
        | Shared.Types.MFAResult.Throttle ->
            {
                model with
                    Error = Some "Too many attempts. Please wait 30s and try again."
                    Code = CodeEntry.resetText model.Code
                    ReSent = true
            }, Cmd.none
        | Shared.Types.MFAResult.Incorrect ->
            {
                model with
                    Error = Some "The code you have entered is incorrect."
                    Code = CodeEntry.resetText model.Code
                    ReSent = true
            }, Cmd.none
        | Shared.Types.MFAResult.Success newUser ->
            let cmd =
                Cmd.OfAsync.either
                    (Storage.store Storage.USER_STORAGE_KEY) 
                    newUser 
                    (fun _ -> LoggedIn)
                    StorageError
            model, cmd
    | LoggedIn ->
        model, Routing.PageRoute.Home |> Routing.PageRoute.toPath |> Elmish.Navigation.Navigation.newUrl
    | MFAStatusCheckResult mode ->
        match mode with
        | MFAMode.Email recheck ->
            { model with Mode = mode },
            if recheck then
                Cmd.none
            else
                Routing.PageRoute.Home |> Routing.PageRoute.toPath |> Elmish.Navigation.Navigation.newUrl
        | MFAMode.App opt ->
            {
                model with
                    Mode = MFAMode.App(opt |> Option.map (fun (k,url) -> k, QRC.getDataUrl url))
            }, Cmd.none
    | UpdateMFASeed ->
        model,
        match model.Mode with
        | MFAMode.App None -> Cmd.none
        | MFAMode.App (Some (key,_)) -> Calls.updateMFASeed key
        | MFAMode.Email _ -> Cmd.none
    | UpdatedMFASeed _ ->
        { model with Mode = MFAMode.App None }, Cmd.none

let renderSetup mfa dispatch =
    [
        jQueryUI.illy_info [
            p [] [ str "Your administrators are enforcing application-based Multi-Factor Authentication." ]
            p [] [
                str "Please install an application, e.g. Google Authenticator on a device (such as a mobile phone) "
                str "and scan this QR Code. Once scanned, click the Accept and Continue button."
            ]
            p [] [ str "You will need to use this application the next time you log in to generate your MFA code." ]
        ]
        div [ Style [ TextAlign TextAlignOptions.Center ] ] [
            img [ Src <| snd mfa ]
        ]
        UI.button "Accept and Continue" UpdateMFASeed dispatch
    ]

let renderEnterCode model dispatch =
    [
        match model.Mode with
        | MFAMode.Email false ->
            yield em [] [ str "Redirecting you..." ]
        | MFAMode.Email true ->
            if model.ReSent then
                yield div [] [ str "Your code has expired. A new verification code has been re-generated and sent to you." ]

            yield em [] [ str "Please note it may take a few minutes for the email to arrive" ]
            yield span [] [
                i [ Id "twofactorInfo"; ClassName "fa fa-info fa-lg"; Style [ MarginLeft "0.5em" ] ] []
                |> Tooltip.create [ str "Please check your inbox for the verification code. If you haven't received this email please refresh your inbox and check your spam folder." ]
            ]
        | MFAMode.App _ ->
            yield em [] [ str "You should find this in your Authenticator app" ]

        yield CodeEntry.render false model.Code (CodeMsg >> dispatch)

        match model.Error with
        | None -> ()
        | Some err -> yield div [ Id "loginError"; ClassName "ui-state-error" ] [ str err ]

        yield UI.buttonWithDisabled (model.Code.Control.AllowSubmit = false) "Submit Validation Code" Submit dispatch
        yield a [ OnClick(fun _ -> dispatch ClearCode)] [ str "Clear" ]
    ]

let renderChrome content =
    div [ Id "mfa"; ClassName "login_theme" ] [
        div [ Id "wrap" ] [
            form [ Id "form" ] [
                div [ Id "profile-management" ] [
                    yield h1 [] [
                        FontAwesome.solid "lock" [] []
                        str " Multi-factor Authentication"
                    ]
                    yield! content
                ]
            ]
        ]
    ]

let view model dispatch =
    renderChrome
        (
            match model.Mode with
            | MFAMode.App (Some mfa) -> renderSetup mfa dispatch
            | _ -> renderEnterCode model dispatch
        )